// src/store/slices/categorySlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/client';
import { CategoryItem } from 'src/api/types';

interface CategoriesState {
  items: CategoryItem[];
  error: string | null;
  loading: boolean;
  loaded: boolean;
  loadedForUserId: number | null;
  requestedForUserId: number | null;
  failedForUserId: number | null;
  requestId: string | null;
}

const initialState: CategoriesState = {
  items: [],
  error: null,
  loading: false,
  loaded: false,
  loadedForUserId: null,
  requestedForUserId: null,
  failedForUserId: null,
  requestId: null,
}

export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (params: { user_id?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.getCategories({
        page_size: 100,
        user_id: params.user_id,
      });
      return response.items;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch categories');
    }
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearCategories: (state) => {
      state.items = [];
      state.error = null;
      state.loading = false;
      state.loaded = false;
      state.loadedForUserId = null;
      state.requestedForUserId = null;
      state.failedForUserId = null;
      state.requestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state, action) => {
        const requestedUserId = action.meta.arg.user_id ?? null;
        if (state.loadedForUserId !== requestedUserId) {
          state.items = [];
          state.loaded = false;
          state.loadedForUserId = null;
        }
        state.loading = true;
        state.error = null;
        state.requestedForUserId = requestedUserId;
        state.failedForUserId = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.items = action.payload;
        state.loaded = true;
        state.loadedForUserId = action.meta.arg.user_id ?? null;
        state.requestedForUserId = null;
        state.failedForUserId = null;
        state.requestId = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload as string;
        state.requestedForUserId = null;
        state.failedForUserId = action.meta.arg.user_id ?? null;
        state.requestId = null;
      });
  }
});

export const { clearCategories } = categoriesSlice.actions;
export default categoriesSlice.reducer;
